import React, { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { TourType, getTourById } from '../../config/TourConfigurations';

/**
 * Props for the TourRunner component
 */
interface TourRunnerProps {
    /** The type of tour to run */
    tourType: TourType | null;
    /** Callback when the tour ends */
    onTourEnd: () => void;
}

/**
 * Component that handles the execution of tours using driver.js.
 * It manages navigation if required by the tour configuration.
 *
 * @param props - The props for the component
 * @returns Null as this component doesn't render any UI
 */
const TourRunner: React.FC<TourRunnerProps> = ({ tourType, onTourEnd }) => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (tourType) {
            const tourConfig = getTourById(tourType);
            if (!tourConfig) {
                console.error(`Tour configuration not found for: ${tourType}`);
                onTourEnd();
                return;
            }

            const startTour = (config: any) => {
                const driverObj = driver({
                    showProgress: true,
                    animate: true,
                    doneBtnText: 'Terminé',
                    nextBtnText: 'Suivant',
                    prevBtnText: 'Précédent',
                    onDestroyed: onTourEnd,
                    steps: config.steps,
                    overlayColor: 'rgba(0, 0, 0, 0.6)',
                    stagePadding: 5,
                    allowClose: true,
                    allowKeyboardControl: true,
                    smoothScroll: true
                });

                driverObj.drive();
            };

            if (tourConfig.requiredPath && location.pathname !== tourConfig.requiredPath) {
                navigate(tourConfig.requiredPath);
                // Wait for navigation and DOM update
                setTimeout(() => startTour(tourConfig), 300);
            } else {
                // If already on the page or no path required, start immediately
                // Use a small timeout to ensure DOM is ready if we just mounted
                setTimeout(() => startTour(tourConfig), 100);
            }
        }
    }, [tourType, navigate, onTourEnd, location.pathname]);

    return null;
};

export default TourRunner;
